import type { FC } from 'react'
import type { BakeryNow, MenuItem } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatClock, toMinutes } from '@/lib/hours'
import { formatMoney, formatPrice, toCents } from '@/lib/format'
import { usePresence } from '@/hooks/use-presence'
import { useFocusTrap } from '@/hooks/use-focus-trap'
import { useBakeryClock } from '@/hooks/use-bakery-clock'
import { useLockBodyScroll } from '@/hooks/use-lock-body-scroll'
import { useCart } from '@/components/order/cart-provider'
import { Photo } from '@/components/generals/photo'
import { Button } from '@/components/generals/button'
import { StatusTag } from '@/components/generals/status-tag'
import { ALLERGENS } from '@/components/generals/allergen-markers'
import { QuantityStepper } from '@/components/generals/quantity-stepper'

interface ItemSheetProps {
    item: MenuItem | null
    onClose: () => void
}

const upcomingBatch = (item: MenuItem, now: BakeryNow | null): string | null => {
    if (!item.nextBatchTime || !now) return null
    if (toMinutes(item.nextBatchTime) <= now.minutes) return null

    return formatClock(item.nextBatchTime)
}

const availabilityNote = (item: MenuItem, now: BakeryNow | null): string => {
    const batch = upcomingBatch(item, now)
    const later = item.orderable ? ' می‌توانید آن را برای روز دیگری پیش‌سفارش دهید.' : ''

    switch (item.status) {
        case 'fresh':
            return batch ? `همین حالا روی پیشخوان است و پخت بعدی ساعت ${batch} از تنور درمی‌آید.` : 'همین حالا روی پیشخوان است.'
        case 'selling-fast':
            return batch ? `امروز رو به اتمام است. پخت بعدی ساعت ${batch} از تنور درمی‌آید.` : 'امروز رو به اتمام است، پس زودتر سر بزنید.'
        case 'sold-out':
            return batch ? `فعلاً تمام شد. پخت بعدی ساعت ${batch} از تنور درمی‌آید.${later}` : `برای امروز تمام شد.${later}`
        default:
            return `در پخت امروز نیست.${later}`
    }
}

// Larger photo, ingredients, allergens and an "Add to basket" button for one menu item.
export const ItemSheet: FC<ItemSheetProps> = ({ item, onClose }) => {
    const open = item !== null
    const now = useBakeryClock()
    const { add } = useCart()
    const panelRef = useRef<HTMLDivElement>(null)
    const [current, setCurrent] = useState<MenuItem | null>(item)
    const [quantity, setQuantity] = useState(1)
    const { mounted, state } = usePresence(open, 320)

    useEffect(() => {
        if (!item) return

        setCurrent(item)
        setQuantity(1)
    }, [item])

    useLockBodyScroll(open)
    useFocusTrap(panelRef, open && mounted, onClose)

    if (!mounted || !current) return null

    const lineTotal = toCents(current.price) * quantity / 100

    return (
        <div className='fixed inset-0 z-50'>
            <div className='ld-overlay absolute inset-0 bg-crust/45' data-state={state} onClick={onClose} aria-hidden='true' />

            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby='item-sheet-title'
                tabIndex={-1}
                data-state={state}
                className='ld-sheet absolute inset-x-0 bottom-0 flex max-h-[92svh] flex-col overflow-hidden bg-background outline-none md:inset-y-0 md:end-0 md:start-auto md:max-h-none md:w-[30rem] lg:w-[34rem]'
            >
                <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain'>
                    <div className='relative'>
                        <Photo
                            src={current.image}
                            alt={current.imageAlt}
                            sizes='(min-width: 1024px) 34rem, (min-width: 768px) 30rem, 100vw'
                            className='aspect-[4/3] md:aspect-[4/5]'
                            reveal={false}
                            fallback={current.name}
                        />

                        <button
                            type='button'
                            onClick={onClose}
                            aria-label='بستن'
                            className='absolute top-4 end-4 inline-flex size-11 cursor-pointer items-center justify-center rounded-full bg-background/90 text-foreground transition-colors duration-300 hover:bg-background focus-visible:outline-2 focus-visible:outline-amber'
                        >
                            <X className='size-5' aria-hidden='true' />
                        </button>
                    </div>

                    <div className='px-6 pt-7 pb-8 sm:px-8'>
                        <div className='flex flex-wrap items-center justify-between gap-3'>
                            <p className='label-caps text-crust-soft' {...(current.categoryId ? { 'data-mdlfy-cms': `menu_categories:${current.categoryId}:name` } : {})}>{current.categoryName}</p>
                            {current.status && <StatusTag status={current.status} />}
                        </div>

                        <div className='mt-3 flex items-baseline justify-between gap-6'>
                            <h2 id='item-sheet-title' className='font-heading text-[2.25rem] leading-[1.05] font-normal'>
                                <span data-mdlfy-cms={`menu_items:${current.id}:name`}>{current.name}</span>
                            </h2>

                            <p className='shrink-0 text-lg tabular-nums'>{formatPrice(current.price)}</p>
                        </div>

                        {current.description && (
                            <p className='mt-4 leading-relaxed text-crust-soft' data-mdlfy-cms={`menu_items:${current.id}:description`}>{current.description}</p>
                        )}

                        <p className={cn('mt-5 border-s-2 ps-4 text-[0.9375rem] leading-relaxed', current.status === 'sold-out' || !current.status ? 'border-line-strong text-crust-soft' : 'border-amber text-foreground')}>
                            {availabilityNote(current, now)}
                        </p>

                        {current.ingredients && (
                            <div className='mt-8 border-t border-line pt-6'>
                                <h3 className='label-caps text-crust-soft'>مواد اولیه</h3>
                                <p className='mt-3 text-[0.9375rem] leading-relaxed' data-mdlfy-cms={`menu_items:${current.id}:ingredients`}>{current.ingredients}</p>
                            </div>
                        )}

                        <div className='mt-6 border-t border-line pt-6'>
                            <h3 className='label-caps text-crust-soft'>آلرژن‌ها</h3>

                            <ul className='mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-[0.9375rem]'>
                                {ALLERGENS.map(entry => {
                                    const contains = current.allergens[entry.key]

                                    return (
                                        <li key={entry.key} className={cn('flex items-center gap-2.5', !contains && 'text-crust-soft')}>
                                            <span className={cn('size-1.5 shrink-0 rounded-full', contains ? 'bg-foreground' : 'border border-line-strong')} aria-hidden='true' />
                                            {contains ? `حاوی ${entry.name}` : `بدون ${entry.name}`}
                                        </li>
                                    )
                                })}
                            </ul>

                            {current.vegan && <p className='mt-3 text-[0.9375rem]'>گیاهی</p>}

                            <p className='mt-4 text-xs leading-relaxed text-crust-soft'>
                                همه‌چیز در یک آشپزخانه تهیه می‌شود که در آن با مغزها، کنجد، لبنیات، تخم‌مرغ و گلوتن هم کار می‌کنیم.
                            </p>
                        </div>
                    </div>
                </div>

                <div className='shrink-0 border-t border-line bg-background px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-8'>
                    {current.orderable ? (
                        <div className='flex items-center gap-3'>
                            <QuantityStepper value={quantity} onChange={setQuantity} min={1} label={current.name} />

                            <Button
                                className='flex-1'
                                onClick={() => {
                                    add(current, quantity)
                                    onClose()
                                }}
                            >
                                افزودن به سبد
                                <span className='tabular-nums opacity-80'>{formatMoney(lineTotal)}</span>
                            </Button>
                        </div>
                    ) : (
                        <p className='py-2 text-sm leading-relaxed text-crust-soft'>
                            این قلم پشت پیشخوان تازه آماده می‌شود و پیش‌سفارش ندارد. وقتی تشریف آوردید، همان‌جا سفارشش بدهید.
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}
